<script setup lang="ts">
import type { AppInputState } from '@shared/ui/inputs'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { AppRoute } from '@shared/config'
import { AppText, AppTextLink } from '@shared/ui/typography'
import { AppMultiSelect, AppSelect } from '@shared/ui/selects'
import { ModalConfirmation } from '@shared/ui/modals'
import { AppInput } from '@shared/ui/inputs'
import { CardAdmin } from '@shared/ui/cards'
import { BtnPrimary } from '@shared/ui/buttons'

import UserDetailActions from './UserDetailActions.vue'
import { useUserEditPage } from '../model/user-edit.hook'

defineOptions({
  name: 'UserDetailsPage',
})

const dayjs = useDayjs()

const {
  refName,
  refEmail,
  refStatus,
  refRoles,
  refTeams,
  refSlack,
  roleOptions,
  statusOptions,
  teamOptions,
  user,
  isNotFound,
  isSaving,
  isSaveDisabled,
  canManage,
  isDeleteOpen,
  load,
  save,
  askDelete,
  cancelDelete,
  confirmDelete,
} = useUserEditPage()

const nameState = computed<AppInputState | undefined>(() => (refName.value.trim() ? undefined : 'error'))

const formatDateTime = (value: string | null | undefined): string =>
  value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

onMounted(() => {
  void load()
})
</script>

<template>
  <div
    v-if="isNotFound"
    class="user-edit__empty"
  >
    <AppText name="body1-medium">
      User not found
    </AppText>
    <AppText
      name="body2"
      color="slate-500"
    >
      The address does not point at a user that exists.
    </AppText>
    <AppTextLink
      name="body2"
      :href="AppRoute.Users"
    >
      Back to users
    </AppTextLink>
  </div>

  <div
    v-else-if="user"
    class="user-edit"
  >
    <div class="user-edit__toolbar">
      <AppText
        name="h3"
        color="slate-900"
      >
        User / {{ user.name }}
      </AppText>
      <div class="user-edit__toolbar-actions">
        <BtnPrimary
          text="Save"
          color="emerald"
          element-width="auto"
          :disabled="isSaveDisabled || isSaving"
          @click="save"
        />
        <UserDetailActions
          v-if="canManage"
          @click:delete="askDelete"
        />
      </div>
    </div>

    <CardAdmin
      title="User info"
      fullwidth
    >
      <div class="user-edit__card">
        <div class="user-edit__fields">
          <div class="user-edit__info-row">
            <AppText
              name="body2-medium"
              color="slate-500"
            >
              ID
            </AppText>
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ user.id }}
            </AppText>
          </div>

          <AppInput
            v-model="refName"
            label="Name"
            placeholder="Enter name"
            :state="nameState"
            :disabled="!canManage"
          />

          <AppInput
            v-model="refEmail"
            type="email"
            label="Email"
            placeholder="Enter email"
            :disabled="!canManage"
          />

          <AppMultiSelect
            v-model="refRoles"
            :options="roleOptions"
            label="Roles"
            placeholder="Select roles"
            :disabled="!canManage"
            clearable
          />

          <AppSelect
            v-model="refStatus"
            :options="statusOptions"
            label="Status"
            placeholder="Select status"
            :disabled="!canManage"
          />

          <AppMultiSelect
            v-model="refTeams"
            :options="teamOptions"
            label="Teams"
            placeholder="Select teams"
            :disabled="!canManage"
            clearable
          />

          <AppInput
            v-model="refSlack"
            label="Slack"
            placeholder="Enter Slack ID"
            :disabled="!canManage"
          />

          <div class="user-edit__info-row">
            <AppText
              name="body2-medium"
              color="slate-500"
            >
              Email verified at
            </AppText>
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ formatDateTime(user.email_verified_at) }}
            </AppText>
          </div>

          <div class="user-edit__info-row">
            <AppText
              name="body2-medium"
              color="slate-500"
            >
              Updated at
            </AppText>
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ formatDateTime(user.updated_at) }}
            </AppText>
          </div>

          <div class="user-edit__info-row">
            <AppText
              name="body2-medium"
              color="slate-500"
            >
              Created at
            </AppText>
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ formatDateTime(user.created_at) }}
            </AppText>
          </div>
        </div>
      </div>
    </CardAdmin>
  </div>

  <ModalConfirmation
    :show="isDeleteOpen"
    mode="delete"
    title="Delete user?"
    detail="Are you sure you want to delete this user? All of data will be permanently removed. This action cannot be uncompleted."
    button-title="Delete user"
    :loading="isSaving"
    @click:confirm="confirmDelete"
    @click:cancel="cancelDelete"
  />
</template>

<style scoped lang="scss" src="./UserDetailsPage.scss" />
